import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, TextField, Button, Stack, Alert, IconButton, InputAdornment,
} from '@mui/material';
import {
  ArrowBack, LockOutlined, Visibility, VisibilityOff, KeyOutlined,
} from '@mui/icons-material';
import { C } from '../vps/theme';
import { useAuth } from '../../context/AuthContext';
import { changePassword } from '../../services/userService';

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errorMsg) setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!user?.id) {
      setErrorMsg('Your session is missing an account ID. Please log out and sign in again.');
      return;
    }
    if (!formData.currentPassword) {
      setErrorMsg('Please enter your current password.');
      return;
    }
    if (formData.newPassword.length < 6) {
      setErrorMsg('New password must be at least 6 characters.');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setErrorMsg('New password and confirm password do not match.');
      return;
    }
    if (formData.newPassword === formData.currentPassword) {
      setErrorMsg('New password must be different from your current password.');
      return;
    }

    setLoading(true);
    try {
      await changePassword({
        id: user.id,
        name: user.name,
        password: formData.newPassword,
      });
      setSuccessMsg('Password changed successfully!');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setErrorMsg(err.message || 'Unable to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRelogin = () => {
    logout();
    navigate('/login');
  };

  if (!user) {
    return (
      <Box sx={{ background: C.bgSection, minHeight: '90vh', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="sm">
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: '16px', background: '#FFFFFF', border: `1px solid ${C.border}`, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary, mb: 1.5 }}>
              You're not signed in
            </Typography>
            <Typography sx={{ color: C.textMuted, mb: 3 }}>
              Please sign in to change your account password.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{ background: C.primary, color: '#FFFFFF', fontWeight: 700, borderRadius: '8px', textTransform: 'none', boxShadow: 'none', '&:hover': { background: C.primaryHover } }}
            >
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ background: C.bgSection, minHeight: '90vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="sm">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ color: C.primary, fontWeight: 600, textTransform: 'none', mb: 3, '&:hover': { background: C.primaryLight } }}
        >
          Back to Home
        </Button>

        <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: '16px', background: '#FFFFFF', border: `1px solid ${C.border}` }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: C.textPrimary, mb: 1 }}>
            Change Account Password
          </Typography>
          <Typography variant="body2" sx={{ color: C.textMuted, mb: 3 }}>
            Update your account password for Hosting Services control panel access.
          </Typography>

          {errorMsg && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: '10px' }}
              action={
                !user?.id ? (
                  <Button color="inherit" size="small" onClick={handleRelogin} sx={{ fontWeight: 700, textTransform: 'none' }}>
                    Log out &amp; sign in
                  </Button>
                ) : undefined
              }
            >
              {errorMsg}
            </Alert>
          )}
          {successMsg && <Alert severity="success" sx={{ mb: 3, borderRadius: '10px' }}>{successMsg}</Alert>}

          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField fullWidth label="Current Password" name="currentPassword" type={showCurrent ? 'text' : 'password'} value={formData.currentPassword} onChange={handleChange} required InputProps={{ startAdornment: <InputAdornment position="start"><KeyOutlined sx={{ color: C.textMuted }} /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end">{showCurrent ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
              <TextField fullWidth label="New Password" name="newPassword" type={showNew ? 'text' : 'password'} value={formData.newPassword} onChange={handleChange} required InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: C.textMuted }} /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowNew(!showNew)} edge="end">{showNew ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
              <TextField fullWidth label="Confirm New Password" name="confirmPassword" type={showConfirm ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleChange} required InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: C.textMuted }} /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">{showConfirm ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />

              <Button type="submit" variant="contained" size="large" fullWidth disabled={loading} sx={{ py: 1.5, background: C.primary, color: '#FFFFFF', fontWeight: 700, borderRadius: '10px', textTransform: 'none', boxShadow: 'none', '&:hover': { background: C.primaryHover } }}>
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default ChangePasswordPage;
